import { router } from "@inertiajs/react"

import { logoutHelpBeacon } from "@/lib/helpBeacon"

// The Rails CSRF token from the inertia layout's <meta name="csrf-token">. The
// session-auth action endpoints use standard Rails forgery protection, so every
// mutating POST has to carry this as X-CSRF-Token (Inertia's own XSRF cookie
// scheme isn't what Rails checks).
export function csrfToken() {
  if (typeof document === "undefined") return ""

  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  )
}

// Sign the customer out with a full-page (hard) navigation, NOT an Inertia XHR.
// Devise DELETE /users/sign_out ends the session then 302s to `/`, and a guest
// `/` 302s on to /users/sign_in. Inertia's router.delete follows those redirects
// as XHRs and never leaves the page (the browser stays put and throws the Inertia
// error modal), so sign-out has to be a real HTML form submit — POST +
// _method=delete + the CSRF token — which the browser navigates like any form.
// One shared path for every sign-out button (D8 "identical"; tuw-app:P-007).
export function signOut(event) {
  event?.preventDefault?.()
  event?.stopPropagation?.()
  if (typeof document === "undefined") return
  logoutHelpBeacon()

  const form = document.createElement("form")
  form.method = "post"
  form.action = "/users/sign_out"
  form.style.display = "none"

  const method = document.createElement("input")
  method.type = "hidden"
  method.name = "_method"
  method.value = "delete"
  form.appendChild(method)

  const token = document.createElement("input")
  token.type = "hidden"
  token.name = "authenticity_token"
  token.value = csrfToken()
  form.appendChild(token)

  document.body.appendChild(form)
  form.submit()
}

// Order actions currently on the wire, keyed by order + action. Module-scoped
// on purpose: the guard below has to hold across re-renders and across the
// three pages that post actions, not per component instance.
const inFlight = new Set()

// POST a customer order action to its session endpoint (see Customer::Orders).
// The controller mutates via the shared OrderActions service then redirects back
// to the order page, so Inertia re-renders OrderDetail with the fresh phase /
// permissions and a flash. `onDone` runs once the request settles (closes the
// modal). The current tab rides along so the redirect lands you back where you
// were.
//
// A second call for the same action is dropped while the first is still in
// flight. Every order action is a state transition guarded server-side by the
// phase it starts from, so a double-click used to send the same approve twice
// and land one of two ways, both wrong: the two requests raced and BOTH ran
// (two transitions, the freelancer notified twice, Shopify fulfilled twice on
// the final-approval path), or the second arrived after the first committed,
// failed OrderActions::CustomerReviewed#order_state and redirected last with
// "Project is in the wrong state" over an approval that had in fact worked
// (AU-35 / UPDA-78).
//
// The guard lives here rather than in the dialogs because all three posting
// surfaces — the order dialogs, Approvals, the Projects request review — share
// this one function, and a disabled button is a courtesy, not a guarantee.
//
// `onSuccess` receives the page the visit landed on, before `onDone` runs.
export function postOrderAction(orderId, path, data = {}, { onDone, onSuccess } = {}) {
  const key = `${orderId}/${path}`
  if (inFlight.has(key)) return

  inFlight.add(key)
  router.post(`/projects/${orderId}/${path}`, data, {
    headers: { "X-CSRF-Token": csrfToken() },
    preserveScroll: true,
    onSuccess: (page) => onSuccess?.(page),
    // Inertia fires onFinish for a cancelled or failed visit as well as a
    // successful one, so the key is always released and a real retry after a
    // failure is never blocked.
    onFinish: () => {
      inFlight.delete(key)
      onDone?.()
    },
  })
}

// Tips don't go through Inertia — the /tip endpoint answers JSON with the Stripe
// PaymentIntent client_secret, which the Elements card form then confirms in the
// browser (the webhook records the tip). Returns the client_secret or throws
// with the server's error message.
export async function createTipIntent(orderId, amount, freelancerId) {
  const res = await fetch(`/projects/${orderId}/tip`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ amount, freelancerId }),
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || "We couldn't start the tip. Please try again.")

  return data.clientSecret
}
